import { useEffect, useRef } from "react";

/* ── 8-bit sprite data (1 = filled pixel) ───────────────────────────── */

const SHOOTER = [
  // barrel low (~10°)
  [
    "000111000000",
    "000111000000",
    "001111100000",
    "000111000000",
    "000111000000",
    "000111111111",
    "000111000000",
    "000111000000",
    "000111000000",
    "000111000000",
    "000111000000",
    "001101100000",
    "011000110000",
    "011000110000",
  ],
  // ~30°
  [
    "000111000000",
    "000111000000",
    "001111100001",
    "000111000110",
    "000111011000",
    "000111100000",
    "000111000000",
    "000111000000",
    "000111000000",
    "000111000000",
    "000111000000",
    "001101100000",
    "011000110000",
    "011000110000",
  ],
  // ~55°
  [
    "000111000000",
    "000111001000",
    "001111101000",
    "000111010000",
    "000111010000",
    "000111100000",
    "000111000000",
    "000111000000",
    "000111000000",
    "000111000000",
    "000111000000",
    "001101100000",
    "011000110000",
    "011000110000",
  ],
  // ~80°
  [
    "000111100000",
    "000111100000",
    "001111100000",
    "000111100000",
    "000111100000",
    "000111100000",
    "000111000000",
    "000111000000",
    "000111000000",
    "000111000000",
    "000111000000",
    "001101100000",
    "011000110000",
    "011000110000",
  ],
];

const HOUSE = [
  "011111111000",
  "010000001000",
  "011111111000",
  "011111111000",
  "011111111000",
  "111111111100",
];

const ARM = [
  // back
  ["10000000", "01000000", "00100000", "00010000", "00000000", "00000000"],
  // mid
  ["00000000", "00011000", "00011000", "00011000", "00000000", "00000000"],
  // forward
  ["00000001", "00000010", "00000100", "00001000", "00000000", "00000000"],
];

const CLAY = ["0110", "1111", "1111", "0110"];

const PUFF = [
  ["00100", "01110", "11111", "01110", "00100"],
  ["10001", "01010", "00100", "01010", "10001"],
];

const SHARD = ["11", "11"];
const PEBBLE = ["11"];

const GLYPHS: Record<string, string[]> = {
  "0": ["111", "101", "101", "101", "111"],
  "1": ["010", "110", "010", "010", "111"],
  "2": ["111", "001", "111", "100", "111"],
  "3": ["111", "001", "111", "001", "111"],
  "4": ["101", "101", "111", "001", "001"],
  "5": ["111", "100", "111", "001", "111"],
  "6": ["111", "100", "111", "101", "111"],
  "7": ["111", "001", "010", "010", "010"],
  "8": ["111", "101", "111", "101", "111"],
  "9": ["111", "101", "111", "001", "111"],
  L: ["100", "100", "100", "100", "111"],
  R: ["111", "101", "111", "110", "101"],
};

const BROWN = "#2A1510";
const ORANGE = "#FA5021";

type Tile = { c: HTMLCanvasElement; w: number; h: number };

function raster(rows: string[], scale: number, color: string, flip = false): Tile {
  const cols = rows[0].length;
  const c = document.createElement("canvas");
  c.width = cols * scale;
  c.height = rows.length * scale;
  const g = c.getContext("2d")!;
  g.fillStyle = color;
  for (let y = 0; y < rows.length; y++) {
    for (let x = 0; x < cols; x++) {
      if (rows[y][x] === "1") {
        const px = flip ? cols - 1 - x : x;
        g.fillRect(px * scale, y * scale, scale, scale);
      }
    }
  }
  return { c, w: c.width, h: c.height };
}

export function PixelSkeetScene() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tilesRef = useRef<Record<string, Tile | Tile[]> | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let W = 0;
    let H = 0;
    let scale = 3;
    let mobile = false;
    let bandL = 0;
    let bandR = 0;
    let ground = 0;
    let pebbles: Array<[number, number]> = [];

    /* ── layout + rasterization ─────────────────────────────────── */
    const build = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = Math.max(1, Math.round(rect.width));
      H = Math.max(1, Math.round(rect.height));
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = false;

      mobile = W < 640;
      scale = mobile ? 2 : 3;
      const band = Math.min(860, W);
      bandL = (W - band) / 2;
      bandR = bandL + band;
      ground = H - 26;

      tilesRef.current = {
        shooterL: SHOOTER.map((s) => raster(s, scale, BROWN)),
        shooterR: SHOOTER.map((s) => raster(s, scale, BROWN, true)),
        house: raster(HOUSE, scale, BROWN),
        arm: ARM.map((s) => raster(s, scale, BROWN)),
        clay: raster(CLAY, scale, BROWN),
        puff: PUFF.map((s) => raster(s, scale, ORANGE)),
        shard: raster(SHARD, scale, ORANGE),
        pebble: raster(PEBBLE, scale, BROWN),
      };

      pebbles = [];
      let seed = 1337;
      const rnd = () => ((seed = (seed * 1103515245 + 12345) % 2147483648) / 2147483648);
      for (let x = 6; x < W; x += 34) pebbles.push([x + rnd() * 22, ground + 6 + rnd() * 12]);
    };

    /* ── round state ─────────────────────────────────────────────── */
    let phase = 0; // 0 pull, 1 fly, 2 burst, 3 beat
    let pt = 0;
    let scoreL = 0;
    let scoreR = 0;
    let dir = 1;
    let apex = 1;
    let winner = 0;
    let breakAt = 1;
    let jitter = 0;
    let curRise = 1;
    let shards: Array<{ x: number; y: number; vx: number; vy: number }> = [];

    const PULL = 0.6;
    const FLY = 2.4;
    const BURST = 0.9;
    const BEAT = 0.5;

    const newRound = () => {
      const r = Math.random();
      dir = r < 0.4 ? -1 : r < 0.8 ? 1 : 0;
      apex = 0.55 + Math.random() * 1.15;
      winner = Math.random() < 0.5 ? 0 : 1;
      breakAt = 0.4 + Math.random() * 0.55;
      jitter = (Math.random() - 0.5) * 30;
      shards = [];
    };
    newRound();

    const clayAt = (p: number) => {
      const x0 = W / 2;
      const x1 = dir === 0 ? W / 2 + jitter : dir < 0 ? bandL * 0.5 : bandR + (W - bandR) * 0.5;
      const rise = mobile ? 46 * apex : Math.min(H * 0.88, 300 * apex);
      curRise = rise;
      const wave = dir === 0 ? Math.sin(Math.PI * p) : Math.sin(Math.PI * p * 0.82);
      return {
        x: x0 + (x1 - x0) * p,
        y: ground - 14 - rise * wave,
      };
    };


    const alphaAt = (x: number) => (x > bandL && x < bandR ? 0.25 : 1);

    const put = (t: Tile, x: number, y: number, a: number) => {
      ctx.globalAlpha = a;
      ctx.drawImage(t.c, Math.round(x), Math.round(y));
    };




    let scoreTiles: HTMLCanvasElement | null = null;
    const buildScore = () => {
      const label = `L ${String(scoreL).padStart(2, "0")}   R ${String(scoreR).padStart(2, "0")}`;
      const c = document.createElement("canvas");
      c.width = label.length * scale * 4;
      c.height = scale * 5;
      const g = c.getContext("2d")!;
      g.fillStyle = BROWN;
      let cx = 0;
      for (const ch of label) {
        const rows = GLYPHS[ch];
        if (rows) {
          for (let y = 0; y < 5; y++)
            for (let x = 0; x < 3; x++)
              if (rows[y][x] === "1") g.fillRect(cx + x * scale, y * scale, scale, scale);
          cx += scale * 4;
        } else cx += scale * 2;
      }
      scoreTiles = c;
    };

    /* ── draw ────────────────────────────────────────────────────── */
    const draw = (staticFrame = false) => {
      const T = tilesRef.current;
      if (!T) return;
      ctx.clearRect(0, 0, W, H);

      const peb = T.pebble as Tile;
      for (const [px, py] of pebbles) put(peb, px, py, 0.25);

      const sL = (T.shooterL as Tile[])[0];
      const shooterY = ground - sL.h;
      const lx = mobile ? 2 : Math.max(2, bandL / 2 - sL.w / 2);
      const rx = mobile ? W - sL.w - 2 : Math.min(W - sL.w - 2, bandR + (W - bandR) / 2 - sL.w / 2);

      let p = 0;
      let aim = 0;
      let flying = false;
      if (staticFrame) {
        p = 0.5;
        aim = 3;
        flying = true;
      } else if (phase === 1) {
        p = pt / FLY;
        flying = true;
      }
      if (flying) {
        const c = clayAt(p);
        const rel = (ground - 14 - c.y) / Math.max(1, curRise);
        aim = Math.min(3, Math.max(0, Math.round(rel * 3)));
      }


      put((T.shooterL as Tile[])[aim], lx, shooterY, 1);
      put((T.shooterR as Tile[])[aim], rx, shooterY, 1);

      const house = T.house as Tile;
      const hx = W / 2 - house.w / 2;
      put(house, hx, ground - house.h, alphaAt(W / 2));
      const armIdx = staticFrame ? 2 : phase === 0 ? (pt < PULL * 0.6 ? 0 : 2) : 1;
      put((T.arm as Tile[])[armIdx], hx + house.w - scale * 2, ground - house.h - scale * 4, alphaAt(W / 2));

      if (flying) {
        const c = clayAt(p);
        put(T.clay as Tile, c.x, c.y, alphaAt(c.x));
      }

      if (!staticFrame && phase === 2) {
        if (pt < 0.22) {
          const pf = (T.puff as Tile[])[pt < 0.11 ? 0 : 1];
          const bx = winner === 0 ? lx + sL.w : rx - pf.w;
          put(pf, bx, shooterY + scale, 1);
        }
        const sh = T.shard as Tile;
        for (const s of shards) if (s.y < H) put(sh, s.x, s.y, alphaAt(s.x));
      }

      if (!mobile && scoreTiles) {
        ctx.globalAlpha = 1;
        ctx.drawImage(scoreTiles, Math.round(W - scoreTiles.width - 16), 16);
      }
      ctx.globalAlpha = 1;
    };

    /* ── loop ────────────────────────────────────────────────────── */
    let raf = 0;
    let last = 0;
    let running = false;
    let visible = false;

    const step = (ts: number) => {
      raf = requestAnimationFrame(step);
      if (ts - last < 33) return;
      const dt = Math.min(0.1, (ts - last) / 1000);
      last = ts;
      pt += dt;

      if (phase === 0 && pt >= PULL) {
        phase = 1;
        pt = 0;
      } else if (phase === 1 && pt >= FLY * breakAt) {
        const c = clayAt(breakAt);

        shards = Array.from({ length: 7 }, (_, i) => ({
          x: c.x,
          y: c.y,
          vx: (i - 3) * 26 + (Math.random() - 0.5) * 30,
          vy: -70 - Math.random() * 60,
        }));
        if (winner === 0) scoreL = (scoreL + 1) % 100;
        else scoreR = (scoreR + 1) % 100;
        buildScore();
        phase = 2;
        pt = 0;
      } else if (phase === 2) {
        for (const s of shards) {
          s.x += s.vx * dt;
          s.y += s.vy * dt;
          s.vy += 340 * dt;
        }
        if (pt >= BURST) {
          phase = 3;
          pt = 0;
        }
      } else if (phase === 3 && pt >= BEAT) {
        newRound();
        phase = 0;
        pt = 0;
      }
      draw();
    };

    const start = () => {
      if (running || reduced) return;
      running = true;
      last = performance.now();
      raf = requestAnimationFrame(step);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    build();
    buildScore();
    draw(reduced);

    const io = new IntersectionObserver(
      ([e]) => {
        visible = e.isIntersecting;
        if (visible && !document.hidden) start();
        else stop();
      },
      { threshold: 0 },
    );
    io.observe(wrap);

    const onVis = () => {
      if (document.hidden) stop();
      else if (visible) start();
    };
    document.addEventListener("visibilitychange", onVis);

    let rt: ReturnType<typeof setTimeout>;
    const ro = new ResizeObserver(() => {
      clearTimeout(rt);
      rt = setTimeout(() => {
        build();
        buildScore();
        draw(reduced);
      }, 120);
    });
    ro.observe(wrap);

    return () => {
      stop();
      clearTimeout(rt);
      io.disconnect();
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <div ref={wrapRef} className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
      <canvas ref={canvasRef} className="h-full w-full" aria-hidden="true" />
    </div>
  );
}
